import React from 'react';
import { StageSection, Prose, KeyPoints, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Contracts & Storage',
    tagline: 'Storage is a key-value map, addressed explicitly rather than by layout.',
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - what #[starknet::contract] and #[storage] actually declare
//   Q1 - how Starknet storage differs from Solidity's slot-based model
export const blocks: StageBlock[] = [
    content('hero', () => (
        <StageSection gradientBorder eyebrow="Stage 4 of 8" title="A Starknet contract is a module, not a class">
            <Prose>
                A Cairo contract is written as a module annotated <code>#[starknet::contract]</code>. Inside it, a{' '}
                <code>#[storage]</code> struct declares every persistent variable the contract has, and functions
                marked as external or view entrypoints become the contract's callable interface, conceptually
                similar to Solidity's public functions, but declared explicitly rather than inferred from
                visibility.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('storage-model', () => (
        <StageSection title="Storage: an explicit key-value map">
            <Prose>
                Where the EVM assigns storage variables to numbered slots based on declaration order, Starknet
                storage is a flat key-value map addressed by a <em>storage address</em> derived from hashing the
                variable's name (and, for maps, its keys). There's no packing multiple small variables into one
                slot the way Solidity does; each storage variable and each map entry has its own independently
                addressed location.
            </Prose>
            <FactGrid items={[
                ['Simple variables', 'Read and written through generated storage-pointer accessors tied to a fixed address derived from the variable\'s name.'],
                ['Storage maps', 'Each entry\'s address is derived from hashing the map\'s base address together with the key, conceptually close to Solidity mappings, computed differently.'],
                ['No layout collisions', 'Because addresses come from hashing names rather than sequential slots, there\'s no Solidity-style "storage slot collision" between a proxy and its implementation.'],
            ]} />
        </StageSection>
    )),

    question(1),

    content('classes-and-instances', () => (
        <StageSection title="Classes, declarations, and deployments">
            <Prose>
                Cairo separates a contract's <em>code</em> from its <em>instances</em>. Declaring a compiled contract
                publishes its class, identified by a class hash, onto Starknet. Deploying then creates an instance
                of that class at a specific address with its own independent storage. Multiple deployed contracts
                can share the same declared class while holding completely separate state, the same relationship
                Ethereum approximates with a factory deploying minimal proxies, except here it's how the protocol
                works natively.
            </Prose>
            <KeyPoints items={[
                <>A contract's logic (its class) and its state (its storage) are separate concepts by design, not an upgrade pattern layered on afterward.</>,
                <>This same separation is exactly what makes contract upgrades possible and, done carelessly, exploitable: a topic the upcoming Cairo security challenges cover directly.</>,
            ]} />
        </StageSection>
    )),
];
